import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";

import {
  UIButton,
  UIIcon,
  UINumberField,
  UISelect,
  UITextField,
  UITimeField,
} from "../../../../../ui";
import {
  formatCurrency,
  getShiftEditLocks,
  getShiftOptions,
  getVisibleError,
} from "../helpers";
import { SHIFT_TYPE_VALUES } from "../helpers";
import MarginFields from "../MarginFields";
import PaidTrainingSection from "../PaidTrainingSection";
import { useSelector } from "react-redux";
import messages from "../../../../../../assets/locale/messages";
import { clx } from "../../../../../../utils/Helpers";

const ShiftPanel = ({
  formik,
  role,
  roleIndex,
  shift,
  shiftIndex,
  onRemoveShift,
  onTrainingToggle,
  defaultTrainingBoundaryMeters,
  hasError,
  portalCompanyOngoingEdit,
  companyPortalMode,
  isEditMode,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const t = events?.creatEvent?.defineRolesAndShifts;

  const shiftBaseName = `event_roles_attributes.${roleIndex}.event_role_shifts_attributes.${shiftIndex}`;

  const { organizerShiftLocked, trainingScheduleLocked } = getShiftEditLocks({
    formik,
    shift,
    shiftBaseName,
    portalCompanyOngoingEdit,
    companyPortalMode,
    isEditMode,
  });

  const labels = useMemo(
    () => ({
      shiftOptions: SHIFT_TYPE_VALUES.map((value) => ({
        value,
        label: shared?.shiftTypes?.[value] || value,
      })),
    }),
    [shared],
  );
  const shiftOptions = useMemo(() => getShiftOptions(labels), [labels]);
  const selectedShiftTypes = role.event_role_shifts_attributes
    .map((item, index) => (index === shiftIndex ? null : item.shift_type))
    .filter(Boolean);
  const availableShiftOptions = shiftOptions.filter(
    (option) => !selectedShiftTypes.includes(option.id),
  );

  const checkInHasError = Boolean(
    getVisibleError(formik, `${shiftBaseName}.before_check_in_margin`) ||
      getVisibleError(formik, `${shiftBaseName}.after_check_in_margin`),
  );
  const checkOutHasError = Boolean(
    getVisibleError(formik, `${shiftBaseName}.before_check_out_margin`) ||
      getVisibleError(formik, `${shiftBaseName}.after_check_out_margin`),
  );

  return (
    <div className={clx("shift-panel", { "shift-panel--error": hasError })}>
      <div className="roles-field-grid roles-field-grid--three">
        <UISelect
          label={t.shiftSlot}
          name={`${shiftBaseName}.shift_type`}
          placeholder={t.shiftSlotPlaceholder}
          required
          options={availableShiftOptions}
          value={
            shiftOptions.find((option) => option.id === shift.shift_type) ||
            null
          }
          onChange={(option) => {
            formik.setFieldValue(
              `${shiftBaseName}.shift_type`,
              option?.id || "",
            );
          }}
          helperText={getVisibleError(formik, `${shiftBaseName}.shift_type`)}
          disabled={organizerShiftLocked}
        />
        <UINumberField
          label={t.payrollAmount}
          name={`${shiftBaseName}.payroll_amount`}
          placeholder="0.00"
          required
          min={0}
          disabled={organizerShiftLocked}
          helperText={getVisibleError(
            formik,
            `${shiftBaseName}.payroll_amount`,
          )}
        />
        <UITextField
          label={t.payrollPerDay}
          name={`${shiftBaseName}.payroll_per_day`}
          value={formatCurrency(shift.payroll_per_day)}
          disabled
        />
      </div>

      <div className="shift-panel__subsection">
        <Typography
          className="shift-panel__subtitle text-label-sm"
          component="h3"
        >
          {t.shiftTiming}
        </Typography>
        <div className="roles-field-grid roles-field-grid--two">
          <UITimeField
            label={t.startTime}
            name={`${shiftBaseName}.starts_at`}
            required
            disabled={organizerShiftLocked}
            helperText={getVisibleError(formik, `${shiftBaseName}.starts_at`)}
          />
          <UITimeField
            label={t.endTime}
            name={`${shiftBaseName}.ends_at`}
            required
            disabled={organizerShiftLocked}
            helperText={getVisibleError(formik, `${shiftBaseName}.ends_at`)}
          />
        </div>
      </div>

      <MarginFields
        formik={formik}
        baseName={shiftBaseName}
        checkInHasError={checkInHasError}
        checkOutHasError={checkOutHasError}
        disabled={organizerShiftLocked}
      />

      <PaidTrainingSection
        formik={formik}
        roleIndex={roleIndex}
        shiftIndex={shiftIndex}
        shift={shift}
        onTrainingToggle={onTrainingToggle}
        defaultTrainingBoundaryMeters={defaultTrainingBoundaryMeters}
        organizerShiftLocked={organizerShiftLocked}
        trainingScheduleLocked={trainingScheduleLocked}
      />

      <div className="shift-panel__footer">
        <UIButton
          color="error"
          startIcon={<UIIcon name="trash" size="lg" />}
          variant="text"
          onClick={onRemoveShift}
          disabled={
            organizerShiftLocked ||
            role.event_role_shifts_attributes.length <= 1
          }
          type="button"
        >
          {t.removeShift}
        </UIButton>
      </div>
    </div>
  );
};

ShiftPanel.propTypes = {
  formik: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
  roleIndex: PropTypes.number.isRequired,
  shift: PropTypes.object.isRequired,
  shiftIndex: PropTypes.number.isRequired,
  onRemoveShift: PropTypes.func.isRequired,
  onTrainingToggle: PropTypes.func.isRequired,
  defaultTrainingBoundaryMeters: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  hasError: PropTypes.bool,
  portalCompanyOngoingEdit: PropTypes.bool,
  companyPortalMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

ShiftPanel.defaultProps = {
  hasError: false,
  portalCompanyOngoingEdit: false,
  companyPortalMode: false,
  isEditMode: false,
};

export default ShiftPanel;
